/**
 * Permission constants for role-based access control.
 *
 * Permissions use `target:action` format matching the grant table.
 * Workspace permissions control access to resources within a specific workspace,
 * while organization permissions control tenant-wide administrative functions.
 */

// ============================================================================
// Flat permission constants — single source of truth
// ============================================================================

export const P = {
  // Tenant-level (organization)
  USERS_READ: "users:read",
  USERS_INVITE: "users:invite",
  USERS_DELETE: "users:delete",
  WORKSPACE_READ: "workspace:read",
  WORKSPACE_CREATE: "workspace:create",
  WORKSPACE_DELETE: "workspace:delete",
  BILLING_MANAGE: "billing:manage",
  ORG_ADMIN: "org:admin",
  ORG_ANALYTICS: "org:analytics",
  PROVIDERS_MANAGE: "providers:manage",
  // Group-level (workspace)
  RESOURCE_READ: "resource:read",
  RESOURCE_EDIT: "resource:edit",
  RESOURCE_DELETE: "resource:delete",
  RESOURCE_RUN: "resource:run",
  RESOURCE_ADVANCED: "resource:advanced",
  RESOURCE_HISTORY: "resource:history",
  EXECUTIONS_RETRY: "executions:retry",
  INTEGRATIONS_EDIT: "integrations:edit",
  GROUP_USERS_EDIT: "group_users:edit",
  GROUP_USERS_DELETE: "group_users:delete",
  WORKSPACE_ADMIN: "workspace:admin",
  WORKSPACE_ANALYTICS: "workspace:analytics",
  VARIABLES_EDIT: "variables:edit",
  CASES_READ: "cases:read",
  CASES_CREATE: "cases:create",
  CASES_EDIT: "cases:edit",
  CASES_DELETE: "cases:delete",
  INBOXES_MANAGE: "inboxes:manage",
  LOGS_SEND_TO_SUPPORT: "logs:send_to_support",
  SANDBOXES_RUN: "sandboxes:run",
  // API-key-only scope, never part of a role: only platform administrators can
  // grant it, and an unscoped key does not imply it.
  RESOURCE_DELETE_HISTORY: "resource:delete_history",
} as const;

// ============================================================================
// Structured accessors (for backward compatibility)
// ============================================================================

export const WORKSPACE_PERMISSIONS = {
  READ: P.RESOURCE_READ,
  EDIT: P.RESOURCE_EDIT,
  DELETE: P.RESOURCE_DELETE,
  RUN: P.RESOURCE_RUN,
  ADVANCED: P.RESOURCE_ADVANCED,
  HISTORY: P.RESOURCE_HISTORY,
  RETRY: P.EXECUTIONS_RETRY,
  SANDBOXES_RUN: P.SANDBOXES_RUN,
  ADMIN: {
    INTEGRATIONS_EDIT: P.INTEGRATIONS_EDIT,
    USERS_EDIT: P.GROUP_USERS_EDIT,
    USERS_DELETE: P.GROUP_USERS_DELETE,
    WORKSPACE_ADMIN: P.WORKSPACE_ADMIN,
  },
  ANALYTICS: P.WORKSPACE_ANALYTICS,
  VARIABLES_EDIT: P.VARIABLES_EDIT,
  CASES: {
    READ: P.CASES_READ,
    CREATE: P.CASES_CREATE,
    EDIT: P.CASES_EDIT,
    DELETE: P.CASES_DELETE,
    MANAGE_INBOXES: P.INBOXES_MANAGE,
  },
} as const;

// Permissions that grant access to inspect a tool's definition.
export const RESOURCE_VIEW_PERMS = [
  P.RESOURCE_READ,
  P.RESOURCE_EDIT,
  P.RESOURCE_DELETE,
];

// History is enough to view past runs; run, edit and retry see the same data.
export const PAST_USAGE_PERMS = [
  P.RESOURCE_HISTORY,
  P.RESOURCE_RUN,
  P.RESOURCE_EDIT,
  P.EXECUTIONS_RETRY,
];

// The App library is where apps are run.
export const APP_LIBRARY_PERMS = [P.RESOURCE_RUN, P.RESOURCE_EDIT];

// What lists the workspace's inboxes; create or delete alone sees none.
export const CASES_PERMS = [P.CASES_READ, P.CASES_EDIT, P.INBOXES_MANAGE];

// Each opens a section Manage workspace lists in its own sidenav.
export const MANAGE_WORKSPACE_PERMS = [
  P.WORKSPACE_ADMIN,
  P.GROUP_USERS_EDIT,
  P.GROUP_USERS_DELETE,
  P.VARIABLES_EDIT,
  P.RESOURCE_ADVANCED,
];

// Any one opens the page: Connections and Analytics are its sections too,
// opened from the main sidenav.
export const WORKSPACE_SETTINGS_PERMS = [
  ...MANAGE_WORKSPACE_PERMS,
  P.INTEGRATIONS_EDIT,
  P.WORKSPACE_ANALYTICS,
];

export const ORG_PERMISSIONS = {
  USERS: {
    READ: P.USERS_READ,
    INVITE: P.USERS_INVITE,
    DELETE: P.USERS_DELETE,
  },
  WORKSPACE: {
    READ: P.WORKSPACE_READ,
    WRITE: P.WORKSPACE_CREATE,
    DELETE: P.WORKSPACE_DELETE,
  },
  ORG: {
    BILLING: P.BILLING_MANAGE,
    ADMIN: P.ORG_ADMIN,
    ANALYTICS: P.ORG_ANALYTICS,
  },
  PROVIDERS: {
    MANAGE: P.PROVIDERS_MANAGE,
  },
} as const;

// ============================================================================
// Permission Labels
// ============================================================================

export const PERMISSION_LABELS: Record<string, string> = {
  // Workshop (unified workspace resource permissions)
  [P.RESOURCE_READ]: "View resources",
  [P.RESOURCE_EDIT]: "Create & edit resources",
  [P.RESOURCE_DELETE]: "Delete resources",
  [P.RESOURCE_RUN]: "Run & query resources",
  [P.RESOURCE_ADVANCED]: "API access & keys",
  [P.RESOURCE_HISTORY]: "View past executions",
  [P.EXECUTIONS_RETRY]: "Retry failed executions",
  [P.LOGS_SEND_TO_SUPPORT]: "Send run logs to support",

  // Case management
  [P.CASES_READ]: "View cases",
  [P.CASES_CREATE]: "Create cases",
  [P.CASES_EDIT]: "Edit cases",
  [P.CASES_DELETE]: "Delete cases",
  [P.INBOXES_MANAGE]: "Manage inboxes",
  [P.SANDBOXES_RUN]: "Create sandboxes and run code in them",
  [P.RESOURCE_DELETE_HISTORY]: "Delete run history",

  // Workspace Admin
  [P.INTEGRATIONS_EDIT]: "Manage integrations",
  [P.GROUP_USERS_EDIT]: "Manage workspace members",
  [P.GROUP_USERS_DELETE]: "Remove workspace members",
  [P.WORKSPACE_ADMIN]: "Workspace admin",
  [P.WORKSPACE_ANALYTICS]: "View workspace analytics",
  [P.VARIABLES_EDIT]: "Manage variables & secrets",

  // Organization Users
  [P.USERS_READ]: "View users",
  [P.USERS_INVITE]: "Invite users",
  [P.USERS_DELETE]: "Remove users",

  // Organization Workspaces
  [P.WORKSPACE_READ]: "View workspaces",
  [P.WORKSPACE_CREATE]: "Create workspaces",
  [P.WORKSPACE_DELETE]: "Delete workspaces",

  // Organization
  [P.BILLING_MANAGE]: "Manage billing",
  [P.ORG_ADMIN]: "Organization admin",
  [P.ORG_ANALYTICS]: "View organization analytics",

  // Providers
  [P.PROVIDERS_MANAGE]: "Manage providers & presets",
};

// ============================================================================
// Permission Descriptions
// ============================================================================

export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  // Workshop
  [P.RESOURCE_READ]:
    "View flows, agents, and knowledge bases without running or editing them",
  [P.RESOURCE_EDIT]: "Create, edit, and run flows, agents, and knowledge bases",
  [P.RESOURCE_DELETE]: "Delete flows, agents, and knowledge bases",
  [P.RESOURCE_RUN]:
    "Run-only access to flows, agents, and knowledge bases. Includes app access and completing tasks assigned in apps",
  [P.RESOURCE_ADVANCED]:
    "Manage API keys and configure API access for workspace resources",
  [P.RESOURCE_HISTORY]:
    "View past executions for flows, agents, and knowledge bases (run history, logs, outputs)",
  [P.EXECUTIONS_RETRY]:
    "Retry failed or stopped executions. Does not allow starting new executions",
  [P.LOGS_SEND_TO_SUPPORT]:
    "Send run logs and the exported workflow to Noxus support. Workflows can contain sensitive data",

  // Case management
  [P.CASES_READ]: "View inboxes, cases, tasks, and case timelines",
  [P.CASES_CREATE]: "Create and clone cases manually",
  [P.CASES_EDIT]:
    "Operate cases: update, move, and claim cases, and complete tasks",
  [P.CASES_DELETE]: "Delete cases from inboxes",
  [P.INBOXES_MANAGE]:
    "Configure inboxes: pipeline settings, properties, filtering rules, templates, and test emails",
  [P.RESOURCE_DELETE_HISTORY]:
    "Delete finished runs with their data over the API to free disk space. Cannot be undone; billing is unaffected",

  // Workspace Admin
  [P.INTEGRATIONS_EDIT]: "Connect and configure external integrations",
  [P.GROUP_USERS_EDIT]: "Invite and modify workspace members",
  [P.GROUP_USERS_DELETE]: "Remove members from workspace",
  [P.WORKSPACE_ADMIN]: "Manage the workspace, its roles, and permissions",
  [P.WORKSPACE_ANALYTICS]: "View analytics and usage data for this workspace",
  [P.VARIABLES_EDIT]:
    "Create, update, and delete workspace-scoped variables and secrets",

  // Organization Users
  [P.USERS_READ]: "View all users in the organization",
  [P.USERS_INVITE]: "Invite new users to the organization",
  [P.USERS_DELETE]: "Remove users from the organization",

  // Organization Workspaces
  [P.WORKSPACE_READ]: "View all workspaces",
  [P.WORKSPACE_CREATE]: "Create new workspaces",
  [P.WORKSPACE_DELETE]: "Delete workspaces",

  // Organization
  [P.BILLING_MANAGE]: "Manage billing, subscriptions, and payment methods",
  [P.ORG_ADMIN]: "Manage organization settings, user permissions, and roles",
  [P.ORG_ANALYTICS]:
    "View analytics and usage data across all workspaces in the organization",

  // Providers
  [P.PROVIDERS_MANAGE]: "Access and configure model providers and presets",
};

// ============================================================================
// Permission Categories for UI
// ============================================================================

export interface PermissionCategory {
  key: string;
  label: string;
  description: string;
  permissions: string[];
}

export const WORKSPACE_PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    key: "resources",
    label: "Workshop",
    description: "Permissions for flows, agents, and knowledge bases",
    permissions: [
      P.RESOURCE_READ,
      P.RESOURCE_EDIT,
      P.RESOURCE_DELETE,
      P.RESOURCE_RUN,
    ],
  },
  {
    key: "cases",
    label: "Case Management",
    description: "Permissions for inboxes, cases, and tasks",
    permissions: [
      P.CASES_READ,
      P.CASES_CREATE,
      P.CASES_EDIT,
      P.CASES_DELETE,
      P.INBOXES_MANAGE,
    ],
  },
  {
    key: "executions",
    label: "Executions",
    description: "Permissions for viewing and retrying past executions",
    permissions: [
      P.RESOURCE_HISTORY,
      P.EXECUTIONS_RETRY,
      P.LOGS_SEND_TO_SUPPORT,
    ],
  },
  {
    key: "admin",
    label: "Workspace administration",
    description: "Administrative permissions for managing the workspace",
    permissions: [
      P.RESOURCE_ADVANCED,
      ...Object.values(WORKSPACE_PERMISSIONS.ADMIN),
      P.WORKSPACE_ANALYTICS,
      P.VARIABLES_EDIT,
    ],
  },
];

export const ORG_PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    key: "users",
    label: "User management",
    description: "Permissions for managing organization users",
    permissions: Object.values(ORG_PERMISSIONS.USERS),
  },
  {
    key: "workspaces",
    label: "Workspace management",
    description: "Permissions for managing workspaces",
    permissions: Object.values(ORG_PERMISSIONS.WORKSPACE),
  },
  {
    key: "organization",
    label: "Organization",
    description: "Permissions for managing organization-wide settings",
    permissions: [
      ...Object.values(ORG_PERMISSIONS.ORG),
      ...Object.values(ORG_PERMISSIONS.PROVIDERS),
    ],
  },
];

// ============================================================================
// API Key Scope Categories (subset of workspace permissions for API keys)
// ============================================================================

export const API_KEY_PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    key: "api-key-scopes",
    label: "API key scopes",
    description: "Permissions that can be restricted on API keys",
    permissions: [
      P.RESOURCE_READ,
      P.RESOURCE_EDIT,
      P.RESOURCE_DELETE,
      P.RESOURCE_RUN,
      P.RESOURCE_HISTORY,
      P.EXECUTIONS_RETRY,
      P.CASES_READ,
      P.CASES_CREATE,
      P.CASES_EDIT,
      P.CASES_DELETE,
      P.INBOXES_MANAGE,
      P.SANDBOXES_RUN,
      P.VARIABLES_EDIT,
    ],
  },
];

// Offered only to platform administrators (useCanDeleteHistory); the backend
// refuses the scope from anyone else.
export const API_KEY_ADMIN_PERMISSION_CATEGORY: PermissionCategory = {
  key: "api-key-admin-scopes",
  label: "Platform administration",
  description: "Scopes only a platform administrator can grant",
  permissions: [P.RESOURCE_DELETE_HISTORY],
};

export const API_KEY_ALL_PERMISSION_CATEGORIES: PermissionCategory[] = [
  ...API_KEY_PERMISSION_CATEGORIES,
  API_KEY_ADMIN_PERMISSION_CATEGORY,
];

// ============================================================================
// Default Role Permissions
// ============================================================================

export const DEFAULT_ROLE_PERMISSIONS = {
  WORKSPACE_ADMIN: {
    name: "Admin",
    description: "Full access to all workspace features",
    permissions: [
      P.RESOURCE_EDIT,
      P.RESOURCE_DELETE,
      P.RESOURCE_RUN,
      P.RESOURCE_ADVANCED,
      ...Object.values(WORKSPACE_PERMISSIONS.ADMIN),
      P.WORKSPACE_ANALYTICS,
      P.VARIABLES_EDIT,
      P.LOGS_SEND_TO_SUPPORT,
    ],
  },
  WORKSPACE_EDITOR: {
    name: "Editor",
    description:
      "Can create, edit, and perform advanced operations on resources",
    permissions: [
      P.RESOURCE_EDIT,
      P.RESOURCE_DELETE,
      P.RESOURCE_RUN,
      P.RESOURCE_ADVANCED,
      P.INTEGRATIONS_EDIT,
      P.GROUP_USERS_EDIT,
      P.WORKSPACE_ANALYTICS,
      P.VARIABLES_EDIT,
    ],
  },
  WORKSPACE_READER: {
    name: "Runner",
    description:
      "Run-only access to flows, agents, and knowledge bases, including working case tasks",
    permissions: [P.RESOURCE_RUN, P.CASES_READ],
  },
  WORKSPACE_CASE_WORKER: {
    name: "Case Worker",
    description:
      "Work cases and tasks without access to flows, agents, or knowledge bases",
    permissions: [P.CASES_READ, P.CASES_EDIT],
  },
  ORG_ADMIN: {
    name: "Admin",
    description: "Full administrative access",
    permissions: [
      ...Object.values(ORG_PERMISSIONS.USERS),
      ...Object.values(ORG_PERMISSIONS.WORKSPACE),
      ...Object.values(ORG_PERMISSIONS.ORG),
      ...Object.values(ORG_PERMISSIONS.PROVIDERS),
    ],
  },
  ORG_BASE: {
    name: "Base",
    description: "Standard user with limited administrative capabilities",
    permissions: [
      P.USERS_READ,
      P.WORKSPACE_READ,
      P.WORKSPACE_CREATE,
      P.ORG_ANALYTICS,
    ],
  },
} as const;

// ============================================================================
// Helper Types
// ============================================================================

export type WorkspacePermission =
  | typeof P.CASES_READ
  | typeof P.CASES_CREATE
  | typeof P.CASES_EDIT
  | typeof P.CASES_DELETE
  | typeof P.INBOXES_MANAGE
  | typeof P.RESOURCE_READ
  | typeof P.RESOURCE_EDIT
  | typeof P.RESOURCE_DELETE
  | typeof P.RESOURCE_RUN
  | typeof P.RESOURCE_ADVANCED
  | typeof P.RESOURCE_HISTORY
  | typeof P.EXECUTIONS_RETRY
  | typeof P.LOGS_SEND_TO_SUPPORT
  | typeof P.RESOURCE_DELETE_HISTORY
  | typeof P.WORKSPACE_ANALYTICS
  | typeof P.VARIABLES_EDIT
  | (typeof WORKSPACE_PERMISSIONS.ADMIN)[keyof typeof WORKSPACE_PERMISSIONS.ADMIN];

export type OrgPermission =
  (typeof ORG_PERMISSIONS)[keyof typeof ORG_PERMISSIONS][keyof (typeof ORG_PERMISSIONS)[keyof typeof ORG_PERMISSIONS]];

export type Permission = WorkspacePermission | OrgPermission;

export interface PermissionsState {
  tenant_permissions: Record<string, boolean>;
  group_permissions?: Record<string, boolean>;
}
